import React from 'react';
import { Link } from 'react-router-dom';
import { MdArrowForward } from 'react-icons/md';
import { FiSmartphone, FiUsers, FiTarget, FiTrendingUp } from 'react-icons/fi';
import WebsiteNavbar from '../components/website/WebsiteNavbar';
import WebsiteFooter from '../components/website/WebsiteFooter';
import logo from '../assets/images/logo.png';

const WebsiteLandingPage: React.FC = () => {
  const features = [
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Private Leagues',
      description: 'Create your own league, invite friends with a code, and compete in your circle.',
    },
    {
      icon: <FiTarget className="w-6 h-6" />,
      title: 'Build Your XI',
      description: 'Pick 11 players from real matches. Choose your Captain and Vice-Captain wisely.',
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Live Leaderboards',
      description: 'Track scores in real-time and watch your rank climb as the match unfolds.',
    },
    {
      icon: <FiSmartphone className="w-6 h-6" />,
      title: 'Play Anywhere',
      description: 'Seamlessly manage your teams and leagues on the go from your phone.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0f0d]">
      <WebsiteNavbar />

      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-36 pb-20 px-4 overflow-hidden">
        {/* Background glows */}
        <div className="absolute top-10 right-0 w-[500px] h-[500px] bg-[#00FF85]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00FF85]/3 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
            Fantasy cricket,{' '}
            <span className="text-[#00FF85]">with your friends</span>
          </h1>
          <p className="text-gray-400 text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Create private leagues, build your dream XI, and prove who really knows cricket best. Free to play, no real money involved.
          </p>

          {/* App Download Buttons — hidden until app store listings are ready */}
          {/* <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a href="/app" className="inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black rounded-xl font-semibold text-sm hover:bg-gray-100 transition-all w-56 justify-center">
              <FaApple className="w-6 h-6" />
              <div className="text-left">
                <div className="text-[10px] leading-none text-gray-500 font-normal">Download on the</div>
                <div className="text-base font-bold leading-tight">App Store</div>
              </div>
            </a>
            <a href="/app" className="inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black rounded-xl font-semibold text-sm hover:bg-gray-100 transition-all w-56 justify-center">
              <FaGooglePlay className="w-5 h-5" />
              <div className="text-left">
                <div className="text-[10px] leading-none text-gray-500 font-normal">Get it on</div>
                <div className="text-base font-bold leading-tight">Google Play</div>
              </div>
            </a>
          </div> */}

          {/* Phone Mockup */}
          <div className="relative max-w-xs mx-auto">
            <div className="bg-[#111b18] border-[3px] border-[#1a3a2a] rounded-[2.5rem] p-3 shadow-2xl shadow-[#00FF85]/5">
              <div className="bg-[#0d1a14] rounded-[2rem] overflow-hidden aspect-[9/19] flex flex-col">
                {/* Status bar */}
                <div className="flex items-center justify-between px-6 pt-4 pb-2">
                  <span className="text-white text-xs font-semibold">9:41</span>
                  <div className="flex gap-1">
                    <div className="w-4 h-2 bg-white/30 rounded-sm" />
                    <div className="w-4 h-2 bg-white/30 rounded-sm" />
                    <div className="w-6 h-2 bg-[#00FF85] rounded-sm" />
                  </div>
                </div>
                {/* App header */}
                <div className="px-5 pt-3 pb-4">
                  <div className="flex items-center gap-2 mb-4">
                    <img src={logo} alt="CricClash" className="w-7 h-7 rounded-md object-cover" />
                    <span className="text-white font-bold text-sm">CricClash</span>
                  </div>
                  <div className="text-white font-bold text-lg mb-1">My Leagues</div>
                  <div className="text-gray-500 text-xs">3 active leagues</div>
                </div>
                {/* League cards */}
                <div className="px-4 space-y-2.5 flex-1">
                  {['Office Cricket League', 'Weekend Warriors', 'IPL Friends League'].map((name, i) => (
                    <div key={i} className="bg-[#111b18] border border-[#1a3a2a] rounded-xl p-3.5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-xs font-semibold">{name}</span>
                        <span className="text-[#00FF85] text-[10px] font-bold">LIVE</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-[10px]">{[8, 5, 12][i]} members</span>
                        <span className="text-gray-400 text-[10px]">Rank: #{[2, 1, 4][i]}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Bottom nav */}
                <div className="mt-auto px-4 py-3 border-t border-[#1a3a2a] flex justify-around">
                  {['Home', 'Leagues', 'Teams', 'Profile'].map((tab, i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className={`w-4 h-4 rounded-full ${i === 1 ? 'bg-[#00FF85]' : 'bg-white/10'}`} />
                      <span className={`text-[9px] ${i === 1 ? 'text-[#00FF85]' : 'text-gray-500'}`}>{tab}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Quote */}
      <section className="py-16 px-4 border-y border-[#1a3a2a]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xl sm:text-2xl text-white font-medium leading-relaxed mb-6 italic">
            "We used to argue about cricket over WhatsApp. Now we settle it on CricClash — it's way more fun when there's a leaderboard."
          </p>
          <div className="text-gray-500 text-sm">
            — The kind of experience we're building for
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Everything you need, right on your phone
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto">
              CricClash is built mobile-first. Create leagues, pick teams, and track matches — all from the app.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-[#111b18] border border-[#1a3a2a] rounded-2xl p-7 hover:border-[#00FF85]/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00FF85]/10 border border-[#00FF85]/20 flex items-center justify-center text-[#00FF85] mb-5 group-hover:scale-110 transition-transform">
                  {f.icon}
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-[#111b18]/50 border-y border-[#1a3a2a]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
            Get started in minutes
          </h2>
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              { step: '1', title: 'Download the app', desc: 'Available on iOS and Android' },
              { step: '2', title: 'Create a league', desc: 'Invite friends with a simple code' },
              { step: '3', title: 'Pick your team', desc: 'Build your XI and start competing' },
            ].map((s, i) => (
              <div key={i}>
                <div className="w-12 h-12 rounded-full bg-[#00FF85]/10 border border-[#00FF85]/20 flex items-center justify-center text-[#00FF85] font-bold mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="text-white font-semibold mb-1">{s.title}</h3>
                <p className="text-gray-500 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA - Download */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Download CricClash
          </h2>
          <p className="text-gray-400 mb-10 max-w-lg mx-auto">
            All the fun of fantasy cricket, right in your pocket. Free to download, free to play.
          </p>
          {/* App store buttons — hidden until ready */}
          {/* <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a href="/app" className="inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black rounded-xl font-semibold text-sm hover:bg-gray-100 transition-all w-56 justify-center">
              <FaApple className="w-6 h-6" />
              <div className="text-left">
                <div className="text-[10px] leading-none text-gray-500 font-normal">Download on the</div>
                <div className="text-base font-bold leading-tight">App Store</div>
              </div>
            </a>
            <a href="/app" className="inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black rounded-xl font-semibold text-sm hover:bg-gray-100 transition-all w-56 justify-center">
              <FaGooglePlay className="w-5 h-5" />
              <div className="text-left">
                <div className="text-[10px] leading-none text-gray-500 font-normal">Get it on</div>
                <div className="text-base font-bold leading-tight">Google Play</div>
              </div>
            </a>
          </div> */}
          <Link to="/how-to-play" className="text-[#00FF85] text-sm font-semibold hover:underline inline-flex items-center gap-1">
            Learn how to play <MdArrowForward />
          </Link>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export default WebsiteLandingPage;
